/**
 * `/sites` — paginated table of Sites. The "New site" button opens the
 * `<CreateSiteDialog>` modal; every row links through to `/sites/:id`.
 *
 * The table renders the columns called out in the issue AC: name, slug,
 * status (chip), primary domain, and createdAt. Primary-domain lookup
 * runs per-row via `useDomains(site.id)` — at MVP a workspace has a small
 * number of sites so the parallel domain fetches are cheap. Slice 2 will
 * collapse this to a single denormalised endpoint.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "@tanstack/react-router";
import type { DomainResponse, SiteResponse, SiteStatus } from "@ottopilot/shared";
import { useSites } from "../hooks/useSites";
import { useDomains } from "../hooks/useDomains";
import { CreateSiteDialog } from "../features/sites/CreateSiteDialog";

const STATUS_COLOR: Record<SiteStatus, "default" | "success" | "warning"> = {
  DRAFT: "default",
  LIVE: "success",
  PAUSED: "warning",
  ARCHIVED: "default",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function findPrimaryDomain(domains: DomainResponse[] | undefined): string {
  if (!domains || domains.length === 0) return "—";
  const primary = domains.find((d) => d.isPrimary);
  return primary?.hostname ?? "—";
}

function SiteRow({ site, onClick }: { site: SiteResponse; onClick: () => void }): ReactElement {
  const domains = useDomains(site.id);
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{ cursor: "pointer" }}
      tabIndex={0}
      data-testid="sites-list-row"
      data-site-id={site.id}
    >
      <TableCell data-testid="sites-list-row-name">
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {site.name}
        </Typography>
      </TableCell>
      <TableCell data-testid="sites-list-row-slug">
        <Typography variant="body2" color="text.secondary">
          {site.slug}
        </Typography>
      </TableCell>
      <TableCell data-testid="sites-list-row-status">
        <Chip
          size="small"
          label={site.status}
          color={STATUS_COLOR[site.status]}
          variant={site.status === "LIVE" ? "filled" : "outlined"}
        />
      </TableCell>
      <TableCell data-testid="sites-list-row-primary-domain">
        {domains.isPending ? (
          <CircularProgress size={16} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {findPrimaryDomain(domains.data)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(site.createdAt)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export function SitesListPage(): ReactElement {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);

  const sites = useSites({ page, pageSize });

  const handleRowClick = (site: SiteResponse) => {
    void navigate({ to: "/sites/$siteId", params: { siteId: site.id } });
  };

  return (
    <Stack spacing={3} data-testid="sites-list-page">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h2" data-testid="sites-list-heading">
            Sites
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every site you operate. Click a row to manage pages and domains.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          data-testid="sites-list-new-site-button"
        >
          New site
        </Button>
      </Stack>

      {sites.isError ? (
        <Alert severity="error" data-testid="sites-list-error-alert">
          {sites.error.message}
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Sites" data-testid="sites-list-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Primary domain</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.isPending ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sites.data && sites.data.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="sites-list-empty-state"
                  >
                    <Typography variant="body2" color="text.secondary">
                      No sites yet. Click “New site” to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sites.data?.data.map((site) => (
                  <SiteRow key={site.id} site={site} onClick={() => handleRowClick(site)} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sites.data?.meta ? (
          <TablePagination
            component="div"
            count={sites.data.meta.total}
            page={page - 1}
            onPageChange={(_e, next) => setPage(next + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        ) : null}
      </Paper>

      <CreateSiteDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(site) => void navigate({ to: "/sites/$siteId", params: { siteId: site.id } })}
      />
    </Stack>
  );
}
